// ─── Crédit amortissable rattaché à une ligne prévue (module PUR) ─
// Port de MoPCal (`MoPCal-v3/services/recurrencesBudget.js:56-283`, Cédric,
// 17/08) : « éprouvé en production », repris quasi à l'identique — ce sont
// les deux fonctions exactement, sans rien de spécifique à MoPCal à retirer.
// Aucun accès réseau, testable comme le reste de `services/*`.
//
// `echeancierCredit`/`estDueCredit`/`montantPrevuCredit`/`raisonNonDueCredit`
// sont les adaptateurs qui branchent ce module sur `services/occurrences.js` :
// eux seuls savent qu'une ligne PORTE un crédit, `calculerCredit` l'ignore.

// Crédit amortissable rattaché à une ligne (facultatif). null si non activé.
// { actif, mode:'params'|'mensualite', capital, taux (annuel %), duree (mois), assurance (mensuelle) }
export function normaliserCredit(credit) {
  if (!credit || !credit.actif) return null;
  const num = (v) => Math.round((parseFloat(String(v).replace(',', '.')) || 0) * 100) / 100;
  return {
    actif: true,
    mode: credit.mode === 'params' ? 'params' : 'mensualite',
    // mode 'params' : capital EMPRUNTÉ à l'origine, `dateCapital` = mois de DÉMARRAGE du crédit.
    // mode 'mensualite' : capital RESTANT DÛ à la date `dateCapital`.
    capital: Math.max(0, num(credit.capital)),
    dateCapital: String(credit.dateCapital || '').slice(0, 7),   // mois 'YYYY-MM' (démarrage ou solde de réf.)
    taux: Math.max(0, num(credit.taux)),
    duree: Math.max(0, Math.round(parseFloat(String(credit.duree).replace(',', '.')) || 0)),
    // Différé de capital (mode 'params' uniquement) : X mois d'INTÉRÊTS SEULS AVANT
    // l'amortissement → le crédit dure differe + duree mois.
    differe: Math.max(0, Math.round(parseFloat(String(credit.differe).replace(',', '.')) || 0)),
    // Déblocages progressifs (mode 'params') : [{date:'YYYY-MM', pct}] cumulés ~100 %
    // (la dernière ligne = démarrage). Intérêts intercalaires pendant la mobilisation.
    deblocages: Array.isArray(credit.deblocages)
      ? credit.deblocages
          .map(d => { let s = String(d.date || '').slice(0, 10); if (/^\d{4}-\d{2}$/.test(s)) s += '-01'; return { date: s, pct: Math.max(0, num(d.pct)) }; })
          .filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d.date) && d.pct > 0)
          .sort((a, b) => a.date.localeCompare(b.date))
      : [],
    // Rembourser le capital DÈS les déblocages (amortissement progressif) → exclut le différé.
    rembCapitalDeblocage: !!credit.rembCapitalDeblocage,
    // Paliers (mode 'params') : [{mensualite, duree}] ; le DERNIER segment (durée restante)
    // est auto-calculé. Amortissement à échéances progressives.
    paliers: Array.isArray(credit.paliers)
      ? credit.paliers
          .map(p => ({ mensualite: Math.max(0, num(p.mensualite)), duree: Math.max(0, Math.round(parseFloat(String(p.duree).replace(',', '.')) || 0)) }))
          .filter(p => p.mensualite > 0 && p.duree > 0)
      : [],
    assurance: Math.max(0, num(credit.assurance)),
  };
}

// Échéancier d'amortissement : part intérêts / part capital et capital
// restant dû, mois par mois. Fonction PURE (aucun accès réseau).
//   credit  : cf. normaliserCredit (capital = restant dû à credit.dateCapital)
//   debut   : date d'ancrage de repli si credit.dateCapital est vide ('YYYY-MM-DD' ou 'YYYY-MM')
//   montant : mensualité TOTALE de la ligne — en mode 'mensualite',
//             mensualité hors assurance = montant − assurance.
// Renvoie { echeances[], duree, mensualite, mensualiteTotale, assurance, capital,
//           taux, totalInterets, totalAssurance, coutTotal } ou { invalide } / null.
// Nombre de jours depuis une époque fixe (days-from-civil, déterministe, sans Date).
const _joursCivil = (y, m, d) => { y -= m <= 2 ? 1 : 0; const era = Math.floor((y >= 0 ? y : y - 399) / 400); const yoe = y - era * 400; const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1; const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy; return era * 146097 + doe - 719468; };
const _jours = (s) => { const [y, m, d] = String(s).split('-').map(Number); return (y && m && d) ? _joursCivil(y, m, d) : 0; };

// jour = jour d'échéance du prêt (1-28) — sert au calcul des intérêts intercalaires
// AU JOUR PRÈS quand les déblocages ont une date précise (YYYY-MM-DD).
export function calculerCredit(credit, debut, montant, jour) {
  if (!credit || !credit.actif) return null;
  const r2 = (n) => Math.round(n * 100) / 100;
  const capital = Math.max(0, credit.capital || 0);
  const assurance = Math.max(0, credit.assurance || 0);
  const tm = Math.max(0, credit.taux || 0) / 100 / 12;   // taux mensuel
  if (!capital) return null;
  let mensualite, dureeCible = 0;
  if (credit.mode === 'params') {
    dureeCible = Math.max(1, Math.round(credit.duree || 0));
    if (!dureeCible) return null;
    mensualite = tm > 0 ? capital * tm / (1 - Math.pow(1 + tm, -dureeCible)) : capital / dureeCible;
  } else {
    mensualite = Math.max(0, (montant || 0) - assurance);
    if (mensualite <= 0) return { invalide: 'mensualite', capital, assurance, taux: credit.taux || 0 };
    if (tm > 0 && mensualite <= capital * tm) return { invalide: 'tropFaible', mensualite: r2(mensualite), capital, assurance, taux: credit.taux || 0 };
  }
  mensualite = r2(mensualite);
  const params = credit.mode === 'params';
  const mkAdd = (baseYM, i) => { const [by, bm] = String(baseYM || '').slice(0, 7).split('-').map(Number); if (!by || !bm) return null; let mo = (bm - 1) + i; const yy = by + Math.floor(mo / 12); mo = (mo % 12 + 12) % 12 + 1; return `${yy}-${String(mo).padStart(2, '0')}`; };
  const diffMk = (a, b) => { const [ay, am] = String(a).split('-').map(Number), [by, bm] = String(b).split('-').map(Number); return (by - ay) * 12 + (bm - am); };
  // Déblocages progressifs (params) : liste [{date, pct}] au JOUR PRÈS (YYYY-MM-DD) ;
  // cumulée ~100 % ; la DERNIÈRE = 100 %. (Rétrocompat : YYYY-MM → 1er du mois.)
  const jourP = Math.max(1, Math.min(28, Math.round(Number(jour) || 1)));
  const deblocages = params && Array.isArray(credit.deblocages)
    ? credit.deblocages
        .map(d => { let s = String(d.date || '').slice(0, 10); if (/^\d{4}-\d{2}$/.test(s)) s += '-01'; return { date: s, pct: Number(d.pct) || 0 }; })
        .filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d.date) && d.pct > 0)
        .sort((a, b) => a.date.localeCompare(b.date))
    : [];
  // Remboursement du capital pendant les déblocages → amortissement progressif, différé impossible.
  const rembCapDeb = params && !!credit.rembCapitalDeblocage && deblocages.length > 0;
  const differe = (params && !rembCapDeb) ? Math.max(0, Math.round(credit.differe || 0)) : 0;
  const MAX = params ? dureeCible : 1200;
  // Paliers (params) : mensualités par segment ; le DERNIER segment (durée restante) est
  // auto-calculé pour amortir le capital restant. Incompatible avec le remb. progressif.
  const paliers = (params && !rembCapDeb && Array.isArray(credit.paliers))
    ? credit.paliers
        .map(p => ({ mensualite: Math.max(0, Number(p.mensualite) || 0), duree: Math.max(0, Math.round(Number(p.duree) || 0)) }))
        .filter(p => p.mensualite > 0 && p.duree > 0)
    : [];
  const echeances = [];
  let totalInterets = 0;

  // Amortit `restant0` à partir de l'offset `kStart` (mois via `mkAt`). Avec paliers :
  // chaque palier {mensualite, duree}, puis segment final (durée restante, mensualité
  // calculée sur le restant). Sinon : mensualité constante `mensualite` sur MAX mois.
  const amortir = (restant0, mkAt, kStart) => {
    let restant = restant0, k = kStart, used = 0;
    const pousser = (interet, capAm) => {
      restant = r2(restant - capAm); totalInterets = r2(totalInterets + interet);
      echeances.push({ i: echeances.length + 1, mk: mkAt(k), interet, capital: capAm, mensualite: r2(interet + capAm), assurance, restantDu: restant });
      k++; used++;
    };
    if (paliers.length) {
      for (const pal of paliers) {
        for (let j = 0; j < pal.duree && used < dureeCible && restant > 0.005; j++) {
          const interet = r2(restant * tm);
          let capAm = r2(pal.mensualite - interet);      // peut être négatif (amortissement négatif)
          if (capAm > restant) capAm = restant;
          pousser(interet, capAm);
        }
        if (used >= dureeCible || restant <= 0.005) break;
      }
      const dureeFinale = Math.max(0, dureeCible - used);
      if (restant > 0.005 && dureeFinale > 0) {
        const mensF = r2(tm > 0 ? restant * tm / (1 - Math.pow(1 + tm, -dureeFinale)) : restant / dureeFinale);
        for (let j = 0; j < dureeFinale && restant > 0.005; j++) {
          const interet = r2(restant * tm); let capAm = r2(mensF - interet);
          if (capAm <= 0) break; if (capAm > restant) capAm = restant;
          pousser(interet, capAm);
        }
        // Reliquat d'arrondi → dernière échéance (le segment final doit solder le prêt).
        if (restant > 0.005 && echeances.length) {
          const last = echeances[echeances.length - 1];
          last.capital = r2(last.capital + restant); last.mensualite = r2(last.interet + last.capital); last.restantDu = 0; restant = 0;
        }
      }
    } else {
      for (let j = 0; j < MAX && restant > 0.005; j++) {
        const interet = r2(restant * tm); let capAm = r2(mensualite - interet);
        if (capAm <= 0) break; if (capAm > restant) capAm = restant;
        pousser(interet, capAm);
      }
    }
  };

  if (deblocages.length > 0) {
    let cum = 0;
    const tranches = deblocages.map(d => { cum += d.pct; return { date: d.date, montantCumul: r2(capital * Math.min(cum, 100) / 100) }; });
    const dFirst = tranches[0].date, d100 = tranches[tranches.length - 1].date;   // 100 % = démarrage
    if (!rembCapDeb) {
      // MOBILISATION au JOUR PRÈS : intérêts intercalaires exact/365 sur le capital cumulé
      // débloqué, payés à chaque échéance (jour `jourP`) jusqu'à la 1re échéance >= 100 %.
      const tauxA = credit.taux || 0;
      const releasedAt = (dateStr) => { let rel = 0; for (const t of tranches) if (t.date <= dateStr) rel = t.montantCumul; return rel; };
      // Intérêt exact/365 sur [from, to), découpé aux dates de déblocage (capital constant entre deux).
      const interetJours = (from, to) => {
        const pts = tranches.map(t => t.date).filter(dt => dt > from && dt < to).sort();
        let s = from, acc = 0;
        for (const p of [...pts, to]) { const nb = _jours(p) - _jours(s); if (nb > 0) acc += releasedAt(s) * (tauxA / 100) * nb / 365; s = p; }
        return r2(acc);
      };
      const payDate = (y, m) => `${y}-${String(m).padStart(2, '0')}-${String(jourP).padStart(2, '0')}`;
      const ym = (s) => s.split('-').map(Number);
      // p100 = 1re échéance >= dernier déblocage (100 %).
      let [ly, lm] = ym(d100); let p100 = payDate(ly, lm);
      if (p100 < d100) { lm++; if (lm > 12) { lm = 1; ly++; } p100 = payDate(ly, lm); }
      // Échéances de mobilisation : dates d'échéance (jour) > dFirst et <= p100.
      let [cy, cm] = ym(dFirst); let pd = payDate(cy, cm);
      while (pd <= dFirst) { cm++; if (cm > 12) { cm = 1; cy++; } pd = payDate(cy, cm); }
      // Le prêt s'affiche DÈS sa date de départ (date de démarrage saisie) : mois « à 0 »
      // depuis cette date jusqu'au 1er déblocage (rien n'est encore débloqué → 0).
      const startMk = (credit.dateCapital && /^\d{4}-\d{2}/.test(String(credit.dateCapital))) ? String(credit.dateCapital).slice(0, 7) : String(debut || '').slice(0, 7);
      const firstMobMk = pd.slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(startMk)) {
        let gv = 0;
        for (let mk = startMk; mk < firstMobMk && gv < 600; mk = mkAdd(mk, 1), gv++) {
          echeances.push({ i: echeances.length + 1, mk, interet: 0, capital: 0, mensualite: 0, assurance: 0, restantDu: 0, mobilisation: true, vide: true });
        }
      }
      let prev = dFirst;
      while (pd <= p100) {
        const interet = interetJours(prev, pd);
        if (interet > 0) {
          totalInterets = r2(totalInterets + interet);
          echeances.push({ i: echeances.length + 1, mk: pd.slice(0, 7), interet, capital: 0, mensualite: interet, assurance, restantDu: releasedAt(pd), mobilisation: true });
        }
        prev = pd; cm++; if (cm > 12) { cm = 1; cy++; } pd = payDate(cy, cm);
      }
      // Amortissement à partir du mois SUIVANT p100 (différé optionnel avant), capital total.
      let [ay, am] = ym(p100); am++; if (am > 12) { am = 1; ay++; }
      const anchor = `${ay}-${String(am).padStart(2, '0')}`;
      let k = 0;
      for (let d = 0; d < differe; d++, k++) {
        const mk = mkAdd(anchor, k); const interet = r2(capital * tm); totalInterets = r2(totalInterets + interet);
        echeances.push({ i: echeances.length + 1, mk, interet, capital: 0, mensualite: interet, assurance, restantDu: capital, differe: true });
      }
      amortir(capital, (kk) => mkAdd(anchor, kk), k);
    } else {
      // Amortissement PROGRESSIF dès le 1er déblocage jusqu'à la fin (démarrage + durée).
      const endInc = mkAdd(d100, dureeCible - 1);
      const nTot = diffMk(dFirst, endInc) + 1;
      let outstanding = 0, released = 0, mensC = 0;
      for (let i = 0; i < nTot && (outstanding > 0.005 || released < capital - 0.005); i++) {
        const mk = mkAdd(dFirst, i);
        const inMonth = tranches.filter(x => x.date.slice(0, 7) === mk);   // dates au jour → comparer par mois
        const t = inMonth.length ? inMonth[inMonth.length - 1] : null;     // dernière tranche du mois (cumul max)
        if (t) { const add = r2(t.montantCumul - released); released = t.montantCumul; outstanding = r2(outstanding + add); }
        // Recalcul de la mensualité à chaque nouveau déblocage, sur la durée restante jusqu'à la fin.
        const moisRest = diffMk(mk, endInc) + 1;
        if (t || mensC === 0) mensC = r2(moisRest > 0 ? (tm > 0 ? outstanding * tm / (1 - Math.pow(1 + tm, -moisRest)) : outstanding / moisRest) : outstanding);
        const interet = r2(outstanding * tm); let capAm = r2(mensC - interet);
        if (capAm < 0) capAm = 0; if (capAm > outstanding) capAm = outstanding;
        outstanding = r2(outstanding - capAm); totalInterets = r2(totalInterets + interet);
        echeances.push({ i: echeances.length + 1, mk, interet, capital: capAm, mensualite: r2(interet + capAm), assurance, restantDu: outstanding });
      }
      if (outstanding > 0.005 && echeances.length) {   // reliquat d'arrondi → dernière échéance
        const last = echeances[echeances.length - 1];
        last.capital = r2(last.capital + outstanding); last.mensualite = r2(last.interet + last.capital); last.restantDu = 0;
      }
    }
  } else {
    // Sans déblocage : différé (intérêts seuls) + amortissement (paliers ou constant),
    // ancre = dateCapital||debut (comportement inchangé si ni différé ni paliers).
    const anchor = String(credit.dateCapital || debut || '').slice(0, 7);
    let idx = 0;
    for (let d = 0; d < differe; d++, idx++) {
      const mk = mkAdd(anchor, idx); const interet = r2(capital * tm); totalInterets = r2(totalInterets + interet);
      echeances.push({ i: idx + 1, mk, interet, capital: 0, mensualite: interet, assurance, restantDu: capital, differe: true });
    }
    amortir(capital, (kk) => mkAdd(anchor, kk), idx);
  }
  const duree = echeances.length;
  const totalAssurance = r2(assurance * duree);
  const coutTotal = r2(echeances.reduce((s, e) => s + e.mensualite, 0) + totalAssurance);
  return { echeances, duree, mensualite, mensualiteTotale: r2(mensualite + assurance), assurance, capital, taux: credit.taux || 0, totalInterets, totalAssurance, coutTotal };
}

// ── Adaptateurs pour `services/occurrences.js` ─────────────────────
// Une ligne à crédit n'a ni fréquence ni inflation : son échéancier EST sa
// courbe. Ces trois fonctions sont les seules qui savent qu'une ligne PORTE
// un crédit — `calculerCredit` l'ignore complètement.

// L'échéancier de la ligne, indexé par mois. `null` si le crédit est
// invalide (capital nul, mensualité trop faible…) — la ligne se comporte
// alors comme si elle n'avait aucune échéance, jamais due.
export function echeancierCredit(ligne) {
  const sched = calculerCredit(ligne?.credit, ligne?.debut, ligne?.montant, ligne?.jour);
  if (!sched || sched.invalide) return null;
  const parMois = new Map();
  for (const e of sched.echeances) parMois.set(e.mk, e);
  return { ...sched, parMois };
}

export function estDueCredit(ligne, mk) {
  return !!echeancierCredit(ligne)?.parMois.has(mk);
}

// Mensualité + assurance du mois — 0 pour un mois « vide » (mobilisation pas
// encore commencée) ou hors échéancier.
export function montantPrevuCredit(ligne, mk) {
  const e = echeancierCredit(ligne)?.parMois.get(mk);
  return e ? Math.round(((e.mensualite || 0) + (e.assurance || 0)) * 100) / 100 : 0;
}

// Miroir de `raisonNonDue` (occurrences.js) pour une ligne à crédit.
export function raisonNonDueCredit(ligne, mk) {
  const sched = echeancierCredit(ligne);
  if (!sched || !sched.echeances.length) return { motif: 'creditInvalide' };
  const premier = sched.echeances[0].mk, dernier = sched.echeances[sched.echeances.length - 1].mk;
  if (mk < premier) return { motif: 'avantDebut', mois: premier };
  if (mk > dernier) return { motif: 'apresFin', mois: dernier };
  return null;
}
